import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FilterState } from "@/components/AdvancedFilters";

const ITEMS_PER_PAGE = 20;

export function useExploreArtworks() {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const auctionParam = searchParams.get("auction");
  
  const [category, setCategory] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("newest");
  const [artworkType, setArtworkType] = useState<"all" | "original" | "print">("all");
  const [showOnlyAuctions, setShowOnlyAuctions] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<string>("all");
  const [advancedFilters, setAdvancedFilters] = useState<FilterState>({
    priceRange: [0, 50000],
    technique: "all",
    color: "all"
  });

  useEffect(() => {
    if (typeParam === "original") {
      setArtworkType("original");
    } else if (typeParam === "print") {
      setArtworkType("print");
    } else {
      setArtworkType("all");
    }
    setShowOnlyAuctions(auctionParam === "true");
  }, [typeParam, auctionParam]);

  const { data: filterData } = useQuery({
    queryKey: ["explore-filter-data"],
    queryFn: async () => {
      // Fetch artworks to get available categories and artist IDs
      const { data: artworksData, error: artworksError } = await supabase
        .from("artworks")
        .select("artist_id, category")
        .eq("is_active", true)
        .eq("approval_status", "approved");
      
      if (artworksError) throw artworksError;
      
      // Get unique artist IDs and categories
      const artistIds = [...new Set((artworksData || []).map(a => a.artist_id))];
      const availableCategories = [...new Set((artworksData || []).map(a => a.category).filter(Boolean))];
      
      if (artistIds.length === 0) return { artists: [], availableCategories: [] };
      
      // Fetch only those artists
      const { data: artistsData, error } = await supabase
        .from("public_profiles")
        .select("id, username, full_name")
        .eq("user_type", "artist")
        .eq("is_active", true)
        .in("id", artistIds)
        .order("username");
      
      if (error) throw error;
      return { artists: artistsData || [], availableCategories };
    },
  });

  const artists = filterData?.artists;
  const availableCategories = filterData?.availableCategories || [];

  const { 
    data: artworksData, 
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: ["artworks", category, sortBy, artworkType, showOnlyAuctions, selectedArtist, advancedFilters],
    queryFn: async ({ pageParam = 0 }) => {
      const from = pageParam * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      let query = supabase
        .from("artworks")
        .select(`*, artwork_images (image_url)`, { count: 'exact' })
        .eq("is_active", true)
        .eq("approval_status", "approved")
        .range(from, to);

      if (category !== "all") query = query.eq("category", category);
      if (artworkType === "print") query = query.in("artwork_type", ["print", "print-on-demand"]);
      else if (artworkType === "original") query = query.eq("artwork_type", "original");
      if (selectedArtist !== "all") query = query.eq("artist_id", selectedArtist);

      if (showOnlyAuctions) {
        query = query.eq("auction_enabled", true).gt("auction_end_date", new Date().toISOString());
      }

      if (advancedFilters.priceRange[0] > 0) query = query.gte("price", advancedFilters.priceRange[0]);
      if (advancedFilters.priceRange[1] < 50000) query = query.lte("price", advancedFilters.priceRange[1]);
      if (advancedFilters.technique !== "all") query = query.eq("technique", advancedFilters.technique);
      if (advancedFilters.color !== "all") query = query.eq("dominant_color", advancedFilters.color);

      if (sortBy === "newest") query = query.order("created_at", { ascending: false });
      else if (sortBy === "price_low") query = query.order("price", { ascending: true });
      else if (sortBy === "price_high") query = query.order("price", { ascending: false });

      const { data: arts, error, count } = await query;
      if (error) throw error;

      if (sortBy === "popular") {
        const artworkIds = (arts || []).map((a: any) => a.id);
        if (artworkIds.length > 0) {
          const { data: salesData } = await supabase
            .from("order_items")
            .select("artwork_id, quantity, order_id")
            .in("artwork_id", artworkIds);

          const orderIds = Array.from(new Set((salesData || []).map(item => item.order_id)));
          const { data: completedOrders } = await supabase
            .from("orders")
            .select("id")
            .in("id", orderIds)
            .eq("status", "delivered");

          const completedOrderIds = new Set((completedOrders || []).map(o => o.id));
          const salesMap = new Map<string, number>();
          (salesData || []).forEach((item: any) => {
            if (completedOrderIds.has(item.order_id)) {
              const current = salesMap.get(item.artwork_id) || 0;
              salesMap.set(item.artwork_id, current + (item.quantity || 1));
            }
          });

          (arts || []).forEach((art: any) => {
            art.sales_count = salesMap.get(art.id) || 0;
          });
          arts?.sort((a: any, b: any) => (b.sales_count || 0) - (a.sales_count || 0));
        }
      }

      const artistIds = Array.from(new Set((arts || []).map((a: any) => a.artist_id).filter(Boolean)));
      let profilesMap = new Map<string, any>();
      if (artistIds.length > 0) {
        const { data: profiles } = await supabase
          .from("public_profiles")
          .select("id, username, full_name, avatar_url")
          .in("id", artistIds);
        (profiles || []).forEach((p: any) => profilesMap.set(p.id, p));
      }

      // Fetch min variant prices for print-on-demand artworks
      const podArtworkIds = (arts || [])
        .filter((a: any) => a.artwork_type === 'print-on-demand')
        .map((a: any) => a.id);

      let minPricesMap = new Map<string, number>();
      if (podArtworkIds.length > 0) {
        const { data: variants } = await supabase
          .from('artwork_variants')
          .select('artwork_id, price')
          .in('artwork_id', podArtworkIds)
          .eq('is_active', true);
        
        (variants || []).forEach((v: any) => {
          const current = minPricesMap.get(v.artwork_id);
          if (!current || v.price < current) {
            minPricesMap.set(v.artwork_id, v.price);
          }
        });
      }

      const enriched = (arts || []).map((a: any) => ({
        ...a,
        public_profiles: profilesMap.get(a.artist_id) || null,
        min_variant_price: a.artwork_type === 'print-on-demand' 
          ? minPricesMap.get(a.id) || a.price
          : null,
      }));

      return { artworks: enriched, total: count || 0 };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.artworks.length, 0);
      return loadedCount < lastPage.total ? allPages.length : undefined;
    },
    initialPageParam: 0,
  });

  const artworks = artworksData?.pages.flatMap(page => page.artworks) || [];
  const totalCount = artworksData?.pages[0]?.total || 0;

  const filteredArtworks = artworks.filter((artwork) => {
    if (advancedFilters.minWidth && artwork.width_cm < advancedFilters.minWidth) return false;
    if (advancedFilters.maxWidth && artwork.width_cm > advancedFilters.maxWidth) return false;
    if (advancedFilters.minHeight && artwork.height_cm < advancedFilters.minHeight) return false;
    if (advancedFilters.maxHeight && artwork.height_cm > advancedFilters.maxHeight) return false;
    return true;
  });

  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);
    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    category,
    setCategory,
    sortBy,
    setSortBy,
    artworkType,
    setArtworkType,
    showOnlyAuctions,
    selectedArtist,
    setSelectedArtist,
    advancedFilters,
    setAdvancedFilters,
    artists,
    availableCategories,
    filteredArtworks,
    totalCount,
    isLoading,
    isFetchingNextPage,
    observerTarget,
  };
}
